import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import * as yup from 'yup';

import { PaymentTerm } from '~hooks/usePaymentTerms/models';

export const paymentTermSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
  // Kept as a string so the text field round-trips cleanly; digits-only covers the
  // whole-non-negative-number requirement (0 = due on receipt)
  dueDays: yup
    .string()
    .trim()
    .matches(/^\d+$/, t('form_validation_errors.numbers_only'))
    .required(t('form_validation_errors.required_simple')),
});

export type PaymentTermDto = yup.InferType<typeof paymentTermSchema>;
export type PaymentTermDtoAsyncError = {
  field: keyof PaymentTermDto;
  message: string;
};

const formFieldByApiField: Record<string, keyof PaymentTermDto> = {
  due_days: 'dueDays',
  name: 'name',
};

// A 422 carries per-field messages the form shows inline; any other failure has already
// been toasted by the hook that made the request.
export const getPaymentTermAsyncErrors = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return [];
  }

  const errorsArray = (error.response.data?.error?.errors ?? []) as ModelError_Item[];

  return errorsArray.flatMap(({ field, message }) => {
    const formField = formFieldByApiField[field];

    return formField ? [{ field: formField, message }] : [];
  });
};

export const setPaymentTermSchemaDefaultValues = (paymentTerm?: PaymentTerm) => ({
  name: paymentTerm?.name ?? '',
  dueDays: paymentTerm ? String(paymentTerm.dueDays) : '',
});
